import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Avatar, Box, Button, Chip, CircularProgress, Divider, IconButton, MenuItem,
    Paper, Select, Stack, TextField, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SendIcon from '@mui/icons-material/Send';
import { getTicket, replyTicket, updateTicket, markResolved } from '../../api/supportApi';
import { useUser } from '../../context/userContext';
import { CATEGORIES, PRIORITIES, STATUSES, STATUS_COLOR, PP, fmt } from './supportConstants';

// Ringgit, the way the rest of the app writes it.
const money = (n) => (n == null ? '—' : `RM ${Number(n).toLocaleString('en-MY', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`);

const cardSx = { border: '1px solid #E2E8F0', borderRadius: '12px', p: { xs: 2, md: 2.5 }, bgcolor: '#fff' };

/* ============================================================================
   ONE TICKET — its own page

   The thread is the ticket, so it gets the width. It was a panel beside the list
   and the conversation had nowhere to go. (user 2026-10-03: "click to view
   another page")

   Internal notes are stripped by the API for the person who raised it — they
   never reach this component at all, so there is nothing here that could leak
   them by mistake.
   ========================================================================= */
export default function SupportTicketPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useUser();

    const [t, setT] = useState(null);
    const [loading, setLoading] = useState(true);
    const [draft, setDraft] = useState('');
    const [internal, setInternal] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getTicket(id);
            setT(res.data);
        } catch {
            setT(null);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    const send = async () => {
        if (!draft.trim()) return;
        setBusy(true);
        setError('');
        try {
            const res = await replyTicket({
                supportTicketId: t.supportTicketId,
                body: draft.trim(),
                isInternalNote: internal,
            });
            setT(res.data);
            setDraft('');
            setInternal(false);
        } catch (e) {
            setError(e?.response?.data?.message || 'Could not send that.');
        } finally {
            setBusy(false);
        }
    };

    const patch = async (fields) => {
        setBusy(true);
        try {
            const res = await updateTicket({ supportTicketId: t.supportTicketId, ...fields });
            setT(res.data);
        } catch (e) {
            setError(e?.response?.data?.message || 'Could not update that.');
        } finally {
            setBusy(false);
        }
    };

    const resolve = async () => {
        setBusy(true);
        try {
            await markResolved(t.supportTicketId);
            await load();
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={30} /></Box>;
    }

    if (!t) {
        return (
            <Box sx={{ p: 4, maxWidth: 620, mx: 'auto', textAlign: 'center' }}>
                <Typography sx={{ fontFamily: PP, fontWeight: 700, fontSize: '1.05rem', mb: 1 }}>Not found</Typography>
                <Typography sx={{ fontFamily: PP, fontSize: '0.84rem', color: '#64748B', mb: 2 }}>
                    That ticket does not exist, or it is not yours to read.
                </Typography>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/support')}
                    sx={{ fontFamily: PP, textTransform: 'none' }}>Back to tickets</Button>
            </Box>
        );
    }

    const [bg, fg] = STATUS_COLOR[t.status] || STATUS_COLOR.Closed;
    const mine = t.userId === user?.userId;
    const settled = t.status === 'Resolved' || t.status === 'Closed';

    return (
        <Box sx={{ width: '100%', fontFamily: PP, pb: 6 }}>
            {/* header */}
            <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ mb: 2.5 }}>
                <IconButton size="small" onClick={() => navigate(t.canManage ? '/support/all' : '/support')}
                    sx={{ color: '#64748B', border: '1px solid #E2E8F0', borderRadius: '8px', p: 0.75, mt: 0.25 }}>
                    <ArrowBackIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                        <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#0F172A', fontFamily: PP, lineHeight: 1.25 }}>
                            #{t.supportTicketId} {t.subject}
                        </Typography>
                        <Chip label={t.status} size="small"
                            sx={{ height: 21, fontSize: '0.64rem', fontWeight: 700, bgcolor: bg, color: fg }} />
                        {t.wasReopened && (
                            <Chip label="Reopened" size="small"
                                sx={{ height: 21, fontSize: '0.64rem', fontWeight: 700, bgcolor: '#FEF3C7', color: '#92400E' }} />
                        )}
                    </Stack>
                    <Typography sx={{ fontFamily: PP, fontSize: '0.78rem', color: '#64748B', mt: 0.25 }}>
                        {t.raisedBy} · {t.category} · {t.priority} priority · raised {fmt(t.createdOn)}
                        {t.relatedRef && !t.sale ? ` · about ${t.relatedType} ${t.relatedRef}` : ''}
                    </Typography>
                </Box>
            </Stack>

            {/* The sale this is about, read fresh with the ticket. Enough to answer without
                opening it - who the customer is, how big the system is, what it cost -
                and a way through when that is not enough.
                (user 2026-10-04: "show the info of the customer details and pricing and
                kwp? so that they can understand? and also the sales link that go to the
                page") */}
            {t.sale && (
                <Paper elevation={0} sx={{ ...cardSx, mb: 2 }}>
                    <Stack direction="row" alignItems="flex-start" sx={{ flexWrap: 'wrap', gap: 1 }}>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography sx={{ fontFamily: PP, fontWeight: 700, fontSize: '0.95rem', color: '#0F172A' }}>
                                {t.sale.customerName}
                            </Typography>
                            <Typography sx={{ fontFamily: PP, fontSize: '0.78rem', color: '#64748B' }}>
                                {[t.sale.phoneNumber, t.sale.city, t.sale.state].filter(Boolean).join(' · ')}
                            </Typography>
                        </Box>
                        <Button size="small" variant="outlined" endIcon={<OpenInNewIcon sx={{ fontSize: 15 }} />}
                            onClick={() => navigate(`/accounting/sales/${t.sale.salesId}`)}
                            sx={{ fontFamily: PP, textTransform: 'none', borderRadius: '8px', fontSize: '0.76rem' }}>
                            Sale #{t.sale.salesId}
                        </Button>
                    </Stack>

                    <Box sx={{
                        mt: 1.5, pt: 1.5, borderTop: '1px solid #E2E8F0',
                        display: 'grid', gap: 1.25,
                        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' },
                    }}>
                        {[
                            ['System', t.sale.energy ? `${t.sale.energy} kWp` : '—'],
                            ['Panels', t.sale.panelQty || '—'],
                            ['Scheme', [t.sale.scheme, t.sale.phase].filter(Boolean).join(' · ') || '—'],
                            ['Price', money(t.sale.finalPrice)],
                            ['Add-ons', t.sale.addOnPrice ? money(t.sale.addOnPrice) : '—'],
                            ['Status', t.sale.status || '—'],
                        ].map(([label, value]) => (
                            <Box key={label}>
                                <Typography sx={{ fontFamily: PP, fontSize: '0.68rem', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                                    {label}
                                </Typography>
                                <Typography sx={{ fontFamily: PP, fontSize: '0.84rem', color: '#1E293B', fontWeight: 600 }}>
                                    {value}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    {t.sale.agentName && (
                        <Typography sx={{ fontFamily: PP, fontSize: '0.74rem', color: '#64748B', mt: 1.25 }}>
                            Sold by {t.sale.agentName}
                            {t.sale.paymentMethod ? ` · ${t.sale.paymentMethod}` : ''}
                        </Typography>
                    )}
                </Paper>
            )}

            {error && (
                <Box sx={{ bgcolor: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', p: 1.5, mb: 2 }}>
                    <Typography sx={{ fontFamily: PP, fontSize: '0.82rem', color: '#DC2626' }}>{error}</Typography>
                </Box>
            )}

            {/* desk controls */}
            {t.canManage && (
                <Paper elevation={0} sx={{ ...cardSx, mb: 2 }}>
                    <Stack direction="row" spacing={1.25} sx={{ flexWrap: 'wrap', gap: 1.25 }}>
                        <Select size="small" value={t.status} onChange={e => patch({ status: e.target.value })}
                            sx={{ minWidth: 150, fontFamily: PP, fontSize: '0.8rem' }}>
                            {STATUSES.map(s => <MenuItem key={s} value={s} sx={{ fontFamily: PP, fontSize: '0.8rem' }}>{s}</MenuItem>)}
                        </Select>
                        <Select size="small" value={t.priority} onChange={e => patch({ priority: e.target.value })}
                            sx={{ minWidth: 120, fontFamily: PP, fontSize: '0.8rem' }}>
                            {PRIORITIES.map(p => <MenuItem key={p} value={p} sx={{ fontFamily: PP, fontSize: '0.8rem' }}>{p}</MenuItem>)}
                        </Select>
                        <Select size="small" value={t.category} onChange={e => patch({ category: e.target.value })}
                            sx={{ minWidth: 140, fontFamily: PP, fontSize: '0.8rem' }}>
                            {CATEGORIES.map(c => <MenuItem key={c} value={c} sx={{ fontFamily: PP, fontSize: '0.8rem' }}>{c}</MenuItem>)}
                        </Select>
                        <Button size="small" variant={t.assignedTo === user?.userId ? 'outlined' : 'contained'}
                            onClick={() => patch({ assignedTo: t.assignedTo === user?.userId ? 0 : user?.userId })}
                            sx={{ fontFamily: PP, textTransform: 'none', borderRadius: '8px' }}>
                            {t.assignedTo === user?.userId ? 'Drop it' : 'Take it'}
                        </Button>
                        {t.assignedToName && t.assignedTo !== user?.userId && (
                            <Typography sx={{ fontFamily: PP, fontSize: '0.78rem', color: '#64748B', alignSelf: 'center' }}>
                                with {t.assignedToName}
                            </Typography>
                        )}
                    </Stack>
                </Paper>
            )}

            {/* the thread */}
            <Paper elevation={0} sx={cardSx}>
                <Stack spacing={2}>
                    {(t.messages || []).map(m => (
                        <Stack key={m.supportTicketMessageId} direction="row" spacing={1.5}
                            sx={{
                                p: m.isInternalNote ? 1.25 : 0,
                                borderRadius: m.isInternalNote ? '10px' : 0,
                                bgcolor: m.isInternalNote ? '#FFFBEB' : 'transparent',
                                border: m.isInternalNote ? '1px solid #FDE68A' : 'none',
                            }}>
                            <Avatar src={m.profilePhotoUrl || undefined} sx={{ width: 34, height: 34, fontSize: '0.8rem' }}>
                                {(m.agentName || '?').charAt(0)}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                                    <Typography sx={{ fontFamily: PP, fontWeight: 600, fontSize: '0.82rem', color: '#0F172A' }}>
                                        {m.agentName}
                                    </Typography>
                                    {m.isFromSupport && !m.isInternalNote && (
                                        <Chip label="Support" size="small"
                                            sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: '#0E8FD1', color: '#fff' }} />
                                    )}
                                    {m.isInternalNote && (
                                        <Chip label="Internal note — the desk only" size="small"
                                            sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: '#B45309', color: '#fff' }} />
                                    )}
                                    <Typography sx={{ fontFamily: PP, fontSize: '0.7rem', color: '#94A3B8' }}>
                                        {fmt(m.createdOn)}
                                    </Typography>
                                </Stack>
                                {/* TEXT, never HTML. */}
                                <Typography sx={{
                                    fontFamily: PP, fontSize: '0.86rem', color: '#1E293B', mt: 0.25,
                                    whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                                }}>
                                    {m.body}
                                </Typography>
                            </Box>
                        </Stack>
                    ))}
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Stack direction="row" spacing={1}>
                    <TextField
                        fullWidth size="small" multiline minRows={2} maxRows={10}
                        placeholder={t.canManage ? 'Answer…' : 'Add to your ticket…'}
                        value={draft} onChange={e => setDraft(e.target.value)}
                        inputProps={{ maxLength: 4000 }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontFamily: PP } }}
                    />
                    <Button variant="contained" onClick={send} disabled={busy || !draft.trim()}
                        sx={{ fontFamily: PP, textTransform: 'none', minWidth: 48, borderRadius: '8px' }}>
                        <SendIcon sx={{ fontSize: 18 }} />
                    </Button>
                </Stack>

                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1.25, flexWrap: 'wrap', gap: 1 }}>
                    {t.canManage && (
                        <Button size="small" onClick={() => setInternal(v => !v)}
                            sx={{
                                fontFamily: PP, textTransform: 'none', fontSize: '0.76rem',
                                color: internal ? '#B45309' : '#64748B',
                            }}>
                            {internal ? 'Internal note — only the desk will see this' : 'Make it an internal note'}
                        </Button>
                    )}
                    <Box sx={{ flex: 1 }} />
                    {mine && !settled && (
                        <Button size="small" variant="outlined" onClick={resolve} disabled={busy}
                            sx={{ fontFamily: PP, textTransform: 'none', fontSize: '0.78rem', borderRadius: '8px', color: '#065F46', borderColor: '#A7F3D0' }}>
                            This is solved
                        </Button>
                    )}
                </Stack>
            </Paper>
        </Box>
    );
}
